import { getMoonCalendar } from '@/api/moonCalendar';
import { formatDate } from '@/helper/utils';

import {
  ApiCalendarData,
  ApiCalendarDataFormated,
  EVENTS,
} from '@/@types/calendar';

import {
  CalendarModule,
  CalendarState,
  CalendarMutationTree,
  CalendarActionTree,
  CalendarGetterTree,
} from '@/store/@types/calendar';

const dateShortFormatter = new Intl.DateTimeFormat('en', {
  month: 'short',
  day: 'numeric',
});

const dateFullFormatter = new Intl.DateTimeFormat('en', {
  month: 'long',
  day: 'numeric',
});

const nameFormatter = new Intl.DateTimeFormat('en', {
  weekday: 'long',
});

function getOffsetDateStrBefore(date: Date): string {
  const locDate = new Date(date.getFullYear(), date.getMonth(), 1);

  // 1 - first date, getDay - date num in week
  const firstDayOffset = 1 - locDate.getDay();

  locDate.setDate(firstDayOffset);

  return formatDate(locDate);
}

function getOffsetDateStrAfter(date: Date): string {
  const locDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  // 6 - Sunday idx in js, getDay - date num in week, getDate - days count
  const lastDayOffset = locDate.getDate() + 6 - locDate.getDay();

  locDate.setDate(lastDayOffset);

  return formatDate(locDate);
}

function formatCalendarData({
  currentData,
  yearMonthStr,
}: {
  currentData: ApiCalendarData;
  yearMonthStr: string;
}): ApiCalendarDataFormated {
  const todayDateStr = formatDate(new Date());
  const dateString = formatDate(new Date(currentData.date * 1000));
  const currentDate = new Date(dateString);
  const isInMonth = dateString.includes(yearMonthStr);

  const eventId = currentData?.zodiac.day_type[0];
  const eventType = (EVENTS[eventId] as keyof typeof EVENTS) ?? null;

  const dateShortName = dateShortFormatter
    .format(currentDate)
    .split(' ')
    .reverse()
    .join(' ');

  const dateFullName = dateFullFormatter.format(currentDate).replace(' ', ', ');

  return {
    ...currentData,
    dateString,
    eventType,
    isInMonth,
    dateShortName,
    dateFullName,
    date: currentDate.getDate(),
    weekDay: nameFormatter.format(currentDate),
    isToday: dateString === todayDateStr,
  };
}

const state: CalendarState = {
  dataStorage: {},
};

const mutations: CalendarMutationTree = {
  SET_DATA_STORAGE(state, data) {
    state.dataStorage = {
      ...state.dataStorage,
      ...data,
    };
  },
};

const actions: CalendarActionTree = {
  async getCalendarData({ commit }, dateObj) {
    const dateStart = getOffsetDateStrBefore(dateObj);
    const dateEnd = getOffsetDateStrAfter(dateObj);
    const calendarData = await getMoonCalendar({ dateStart, dateEnd });
    const monthKey = formatDate(dateObj);
    const yearMonthStr = monthKey.slice(0, 7);

    const datesByTimestamp = calendarData.reduce((acc, currentData) => {
      const dateStr = formatDate(new Date(currentData.date * 1000));

      acc[dateStr] = formatCalendarData({ yearMonthStr, currentData });

      return acc;
    }, {} as Record<string, ApiCalendarDataFormated>);

    const data = {
      [monthKey]: datesByTimestamp,
    };

    commit('SET_DATA_STORAGE', data);
  },
};

const getters: CalendarGetterTree = {
  dataStorage: (state) => state.dataStorage,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
} as CalendarModule;
